import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Calendar,
  FileText,
  ShoppingCart,
  DollarSign,
  Edit,
  Pause,
  Play,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Trash2,
  Package,
  Trophy,
  TrendingUp,
  ArrowRightLeft,
  Undo2,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getDistributorById,
  getCreditSummary,
  getDistributorOrders,
  getDistributorPayments,
  suspendDistributor,
  activateDistributor,
  deleteDistributor,
  getDistributors,
  transferAccountBalance,
  refundAccountBalance,
  getAccountTransactions
} from '../../api/wholesale/distributors';
import EditDistributorModal from '../../components/wholesale/EditDistributorModal';
import AccountBalanceModal from '../../components/wholesale/AccountBalanceModal';
import { getCustomerStats } from '../../api/wholesale/analytics';
import { getBalanceHistory } from '../../api/wholesale/reconciliation';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import PeriodFilter from '../../components/common/PeriodFilter';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const DistributorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { periodRange, getDateRangeParams } = useFiscalPeriod();

  const [distributor, setDistributor] = useState(null);
  const [creditSummary, setCreditSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [accountTransactions, setAccountTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAccountBalanceModalOpen, setIsAccountBalanceModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [customerStats, setCustomerStats] = useState(null);
  const [allDistributors, setAllDistributors] = useState([]);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Local period filter state (null = use global, otherwise override)
  const [localPeriod, setLocalPeriod] = useState(null);

  // Current active date range (local override or global)
  const activeDateRange = useMemo(() => {
    // If local period is set (user selected a non-global option), use it
    if (localPeriod) {
      return {
        start_date: localPeriod.start_date,
        end_date: localPeriod.end_date,
        label: localPeriod.label
      };
    }
    // Otherwise, use global period from context
    return {
      start_date: periodRange?.start_date || null,
      end_date: periodRange?.end_date || null,
      label: periodRange?.period_label || 'All Time'
    };
  }, [localPeriod, periodRange]);

  // Handle period filter change
  const handlePeriodChange = useCallback((dateRange) => {
    // If user selects "Global Period" option (isGlobal flag), reset to use global settings
    if (dateRange.isGlobal) {
      setLocalPeriod(null);
    } else {
      setLocalPeriod(dateRange);
    }
  }, []);

  // Determine if user explicitly chose "All Time" (local override with no dates)
  const isExplicitAllTime = localPeriod && !localPeriod.start_date && !localPeriod.end_date;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Build date filter params
    const dateParams = {};
    if (activeDateRange.start_date) {
      dateParams.start_date = activeDateRange.start_date;
    }
    if (activeDateRange.end_date) {
      dateParams.end_date = activeDateRange.end_date;
    }

    // Tell backend to skip global fiscal period fallback when "All Time" is explicitly selected
    if (isExplicitAllTime && !dateParams.start_date && !dateParams.end_date) {
      dateParams.ignore_global_period = true;
    }

    try {
      const [distRes, creditRes, ordersRes, paymentsRes, transactionsRes] = await Promise.all([
        getDistributorById(id),
        getCreditSummary(id, dateParams).catch(() => null),
        getDistributorOrders(id, { limit: 10, ...dateParams }).catch(() => []),
        getDistributorPayments(id, { limit: 10, ...dateParams }).catch(() => []),
        getAccountTransactions(id, { limit: 10 }).catch(() => [])
      ]);

      setDistributor(distRes?.data || distRes);

      // Set balance loading state
      setBalanceLoading(true);
      setCreditSummary(creditRes?.data || creditRes);
      setBalanceLoading(false);

      setOrders(ordersRes?.data || ordersRes || []);
      setPayments(paymentsRes?.data || paymentsRes || []);
      setAccountTransactions(transactionsRes?.data || transactionsRes || []);

      // Fetch customer stats with period filter
      try {
        const statsRes = await getCustomerStats(id, dateParams);
        setCustomerStats(statsRes?.data || null);
      } catch (err) {
        console.warn('Could not fetch customer stats:', err);
      }

      // Fetch balance history (12 months)
      try {
        const historyRes = await getBalanceHistory(id, 12);
        setBalanceHistory(historyRes?.data || []);
      } catch (err) {
        console.warn('Could not fetch balance history:', err);
      }
    } catch (err) {
      console.error('Error fetching distributor:', err);
      setError('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  }, [id, activeDateRange.start_date, activeDateRange.end_date]);

  // Fetch data when id or period changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSuspend = async () => {
    if (!window.confirm('Are you sure you want to suspend this customer?')) return;
    try {
      await suspendDistributor(id, 'Suspended by admin');
      toast.success('Customer suspended');
      fetchData();
    } catch (err) {
      toast.error('Failed to suspend customer');
    }
  };

  const handleActivate = async () => {
    try {
      await activateDistributor(id);
      toast.success('Customer activated');
      fetchData();
    } catch (err) {
      toast.error('Failed to activate customer');
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this customer? This action cannot be undone.'
      )
    )
      return;
    try {
      await deleteDistributor(id);
      toast.success('Customer deleted successfully');
      navigate('/wholesale/distributors');
    } catch (err) {
      toast.error(err.message || 'Failed to delete customer');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !distributor) {
    return (
      <div className="space-y-6">
        <Link
          to="/wholesale/distributors"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Customers
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Customer not found'}
        </div>
      </div>
    );
  }

  const status = distributor.customer_status || 'active';
  const creditLimit = parseFloat(distributor.credit_limit) || 0;

  // Use live-computed outstanding balance from creditSummary (matches list page logic)
  // FALLBACK to distributor.current_credit_balance (stale cached column)
  const creditUsed = parseFloat(
    creditSummary?.outstanding_balance !== undefined
      ? creditSummary.outstanding_balance
      : distributor.current_credit_balance || 0
  );

  // Account Balance: Preference for creditSummary (can be period filtered if needed,
  // but usually we want the current real balance)
  const accountBalance = parseFloat(
    creditSummary?.account_balance !== undefined
      ? creditSummary.account_balance
      : distributor.account_balance || 0
  );

  const creditAvailable = creditLimit - creditUsed;
  const creditUtilization = creditLimit > 0 ? (creditUsed / creditLimit) * 100 : 0;

  const tierColors = {
    bronze: 'bg-amber-100 text-amber-700',
    silver: 'bg-gray-100 text-gray-700',
    gold: 'bg-yellow-100 text-yellow-700',
    platinum: 'bg-purple-100 text-purple-700'
  };

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    suspended: 'bg-red-100 text-red-700',
    inactive: 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/wholesale/distributors"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{distributor.business_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.active}`}
              >
                {status}
              </span>
              {distributor.tier_name && (
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${tierColors[distributor.tier_name?.toLowerCase()] || tierColors.bronze}`}
                >
                  {distributor.tier_name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Period Filter */}
          <PeriodFilter
            onChange={handlePeriodChange}
            showLabel={true}
            compact={false}
            storageKey={`distributor_${id}`}
          />
          <button
            onClick={fetchData}
            className="p-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            title="Refresh data"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        </div>
      </div>

      {/* Period Filter Explanation Banner */}
      {localPeriod && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-blue-900 mb-1">
                Period Filter Active: {activeDateRange.label}
              </h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>
                    <strong>Affected:</strong> Total Order Value, Order Counts, Purchase Performance
                  </span>
                </p>
                <p className="flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  <span>
                    <strong>Not Affected:</strong> Outstanding Balance (always shows all-time unpaid
                    orders)
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={() => setLocalPeriod(null)}
              className="text-blue-600 hover:text-blue-700 p-1"
              title="Clear period filter"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Business Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Business Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <User className="w-4 h-4 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Contact Person</p>
                <p className="font-medium text-gray-900">
                  {distributor.contact_person_name || '-'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-900">{distributor.phone_number || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{distributor.email || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium text-gray-900">
                  {[distributor.business_address, distributor.city, distributor.state]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Balance Summary - Redesigned */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Balance Summary
            {localPeriod && (
              <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {activeDateRange.label}
              </span>
            )}
          </h3>

          {/* Show loading skeleton if creditSummary is loading */}
          {balanceLoading ? (
            <div className="space-y-4">
              <div className="animate-pulse bg-gray-100 rounded-lg p-3 border-2 border-gray-200">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-40 mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
              <div className="animate-pulse bg-gray-100 rounded-lg p-3 border-2 border-gray-200">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-40 mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
              <div className="animate-pulse bg-gray-100 rounded-lg p-3 border-2 border-gray-200">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-40 mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          ) : (
            <>
              {/* Show error if creditSummary failed to load */}
              {!creditSummary && !loading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>Failed to load balance data</span>
                  </div>
                  <button
                    onClick={fetchData}
                    className="mt-2 text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}

              <div className="space-y-4">
                {/* Total Order Value (filtered by period) */}
                <div className="bg-white/10 rounded-lg p-3 border-2 border-blue-200">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm text-blue-700 font-medium">Total Order Value</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-700">
                    ₦{parseFloat(creditSummary?.total_order_value || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    {activeDateRange.label && activeDateRange.label !== 'All Time'
                      ? activeDateRange.label
                      : 'All time order value'}
                  </p>
                </div>

                {/* Account Balance (Prepaid Deposits - NOT linked to orders) */}
                <div className="bg-white/10 rounded-lg p-3 border-2 border-emerald-200">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-emerald-700 font-medium">Account Balance</span>
                      <div className="group relative">
                        <AlertCircle className="w-3 h-3 text-emerald-600 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          Prepaid deposits available for future orders. This is money the customer
                          has paid in advance.
                        </div>
                      </div>
                    </div>
                    {accountBalance > 0 && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => setIsTransferModalOpen(true)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                          title="Transfer to another customer"
                        >
                          <ArrowRightLeft className="w-3 h-3" />
                          Transfer
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => setIsRefundModalOpen(true)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                          title="Refund to customer"
                        >
                          <Undo2 className="w-3 h-3" />
                          Refund
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-2xl font-bold text-emerald-700">
                    ₦{accountBalance.toLocaleString()}
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">Prepaid deposits for future use</p>
                </div>

                {/* Outstanding Balance (Unpaid order amounts) */}
                <div
                  className={`bg-white/10 rounded-lg p-3 border-2 ${creditUsed > 0 ? 'border-amber-200' : 'border-gray-200'}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-1">
                      <span
                        className="text-sm font-medium"
                        style={{ color: creditUsed > 0 ? '#d97706' : '#6b7280' }}
                      >
                        Outstanding Balance
                      </span>
                      <div className="group relative">
                        <AlertCircle
                          className="w-3 h-3 cursor-help"
                          style={{ color: creditUsed > 0 ? '#d97706' : '#6b7280' }}
                        />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          Total unpaid order amounts (all time). This is money the customer owes
                          you. Not affected by period filter.
                        </div>
                      </div>
                    </div>
                    {creditUsed > 0 && (
                      <button
                        onClick={() => setIsAccountBalanceModalOpen(true)}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                      >
                        + Pay
                      </button>
                    )}
                  </div>
                  <div
                    className="text-2xl font-bold"
                    style={{ color: creditUsed > 0 ? '#d97706' : '#6b7280' }}
                  >
                    ₦{creditUsed.toLocaleString()}
                  </div>
                  <p
                    className="text-xs mt-1"
                    style={{ color: creditUsed > 0 ? '#d97706' : '#6b7280' }}
                  >
                    {creditUsed > 0 ? 'Unpaid order amounts' : 'No outstanding balance'}
                  </p>
                </div>

                {/* Credit Limit Info */}
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500">Credit Limit</span>
                    <span className="font-semibold text-gray-900">
                      ₦{creditLimit.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500">Available Credit</span>
                    <span
                      className={`font-semibold ${creditAvailable < 0 ? 'text-red-600' : 'text-emerald-600'}`}
                    >
                      {creditAvailable < 0 ? '-' : ''}₦{Math.abs(creditAvailable).toLocaleString()}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-2">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${creditUtilization > 80 ? 'bg-red-500' : creditUtilization > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.min(creditUtilization, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {creditUtilization.toFixed(1)}% credit utilized
                    </p>
                  </div>

                  <div className="flex justify-between items-center mt-3">
                    <span className="text-sm text-gray-500">Payment Terms</span>
                    <span className="font-medium text-gray-900">
                      {distributor.payment_terms || 30} days
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Account Summary
            {localPeriod && (
              <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {activeDateRange.label}
              </span>
            )}
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Total Orders</span>
              <span className="font-semibold text-gray-900">
                {creditSummary?.total_orders || orders.length || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Pending Orders</span>
              <span className="font-semibold text-amber-600">
                {creditSummary?.pending_orders_count || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Overdue Orders</span>
              <span
                className={`font-semibold ${(creditSummary?.overdue_orders_count || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}
              >
                {creditSummary?.overdue_orders_count || 0}
              </span>
            </div>
            <div className="pt-2 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Member Since</span>
                <span className="font-medium text-gray-900">
                  {distributor.created_at
                    ? new Date(distributor.created_at).toLocaleDateString()
                    : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Performance */}
      {customerStats && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/10 dark:to-teal-900/10 rounded-xl border border-emerald-200 dark:border-emerald-800/30 p-6 transition-colors">
          <h3 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Purchase Performance
            {localPeriod && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full border border-blue-200 dark:border-blue-800/30">
                {activeDateRange.label}
              </span>
            )}
            {customerStats.rank && (
              <span className="ml-2 px-2 py-0.5 bg-emerald-600 dark:bg-emerald-500 text-white text-xs rounded-full shadow-sm">
                Rank #{customerStats.rank}
              </span>
            )}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4 border border-emerald-100 dark:border-emerald-800/30 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                  Total Cartons
                </span>
              </div>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                {parseInt(customerStats.total_cartons || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4 border border-emerald-100 dark:border-emerald-800/30 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Total Value
                </span>
              </div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                ₦{(parseFloat(customerStats.total_value || 0) / 1000000).toFixed(2)}M
              </p>
            </div>
            <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4 border border-emerald-100 dark:border-emerald-800/30 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingCart className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
                  Total Orders
                </span>
              </div>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                {customerStats.total_orders || 0}
              </p>
            </div>
          </div>
          {customerStats.size_breakdown && Object.keys(customerStats.size_breakdown).length > 0 && (
            <div className="mt-4 pt-4 border-t border-emerald-200 dark:border-emerald-800/30">
              <div className="mb-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4" />
                  Cartons by Size
                </h4>
              </div>
              <div className="flex flex-wrap gap-2 pl-6">
                {Object.entries(customerStats.size_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([size, count]) => (
                    <span
                      key={size}
                      className="px-3 py-1.5 bg-white dark:bg-emerald-900/30 rounded-full text-sm border border-emerald-200 dark:border-emerald-700/50 text-emerald-800 dark:text-emerald-200 shadow-sm flex items-center gap-2"
                    >
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {size}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-emerald-400 dark:bg-emerald-600"></span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {count.toLocaleString()}
                      </span>
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Balance History Chart */}
      {balanceHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Balance History (12 Months)
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={balanceHistory} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  tickFormatter={(value) =>
                    new Date(value).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
                  }
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}K`}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value) => [`₦${parseFloat(value).toLocaleString()}`, '']}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={(value) => {
                    const labels = {
                      outstanding_balance: 'Outstanding Balance',
                      account_balance: 'Account Balance',
                      net_balance: 'Net Balance'
                    };
                    return labels[value] || value;
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="outstanding_balance"
                  stroke="#d97706"
                  strokeWidth={2}
                  dot={{ fill: '#d97706', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Outstanding Balance"
                />
                <Line
                  type="monotone"
                  dataKey="account_balance"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Account Balance"
                />
                <Line
                  type="monotone"
                  dataKey="net_balance"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Net Balance"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <p className="text-sm text-gray-500">Current Outstanding</p>
              <p className="text-lg font-bold text-amber-600">
                ₦{(balanceHistory[0]?.outstanding_balance || 0).toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Current Account Balance</p>
              <p className="text-lg font-bold text-emerald-600">
                ₦{(balanceHistory[0]?.account_balance || 0).toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Current Net Position</p>
              <p
                className={`text-lg font-bold ${
                  (balanceHistory[0]?.net_balance || 0) < 0 ? 'text-red-600' : 'text-emerald-600'
                }`}
              >
                {(balanceHistory[0]?.net_balance || 0) < 0 ? '-' : ''}₦
                {Math.abs(balanceHistory[0]?.net_balance || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-gray-500" />
            Recent Orders
            {localPeriod && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {activeDateRange.label}
              </span>
            )}
          </h3>
          <Link
            to={`/wholesale/orders?distributor=${id}`}
            className="text-sm text-emerald-600 hover:text-emerald-700"
          >
            View All
          </Link>
        </div>
        {orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No orders yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Order #</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Date</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.slice(0, 5).map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/wholesale/orders/${order.id}`)}
                  >
                    <td className="px-6 py-4">
                      <span className="text-emerald-600 hover:text-emerald-700 font-medium">
                        {order.order_number}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {order.order_status || order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                      ₦{parseFloat(order.total_amount || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Account Transaction History (Wallet/Ledger) */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-gray-500" />
            Account Transaction History
            <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
              Wallet Flow
            </span>
          </h3>
        </div>
        {accountTransactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No account transactions found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Date</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Type</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">
                    Description
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Amount</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {accountTransactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          tx.transaction_type === 'deposit'
                            ? 'bg-emerald-100 text-emerald-700'
                            : tx.transaction_type === 'withdrawal' ||
                                tx.transaction_type === 'order_payment'
                              ? 'bg-amber-100 text-amber-700'
                              : tx.transaction_type === 'refund'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {tx.transaction_type?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {tx.description || tx.remark || '-'}
                    </td>
                    <td
                      className={`px-6 py-4 text-sm text-right font-medium ${parseFloat(tx.amount) > 0 ? 'text-emerald-600' : 'text-red-600'}`}
                    >
                      {parseFloat(tx.amount) > 0 ? '+' : ''}₦
                      {parseFloat(tx.amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-bold text-gray-900">
                      ₦{parseFloat(tx.balance_after).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Payments */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-gray-500" />
            Recent Payments
            {localPeriod && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {activeDateRange.label}
              </span>
            )}
          </h3>
          <Link
            to={`/wholesale/payments?distributor=${id}`}
            className="text-sm text-emerald-600 hover:text-emerald-700"
          >
            View All
          </Link>
        </div>
        {payments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No payments yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">
                    Reference
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Date</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Method</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.slice(0, 5).map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {payment.payment_number || payment.reference_number || '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {payment.payment_date
                        ? new Date(payment.payment_date).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-700 capitalize">
                      {payment.payment_method?.replace(/_/g, ' ') || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          payment.payment_status === 'verified'
                            ? 'bg-emerald-100 text-emerald-700'
                            : payment.payment_status === 'pending'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {payment.payment_status === 'verified' && (
                          <CheckCircle className="w-3 h-3" />
                        )}
                        {payment.payment_status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-emerald-600">
                      ₦{parseFloat(payment.payment_amount || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-xl border-2 border-red-200 p-6">
        <h3 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Danger Zone
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          These actions are destructive and may have significant consequences. Please proceed with
          caution.
        </p>
        <div className="flex flex-wrap gap-3">
          {status === 'active' ? (
            <button
              onClick={handleSuspend}
              className="inline-flex items-center gap-2 px-4 py-2 text-red-700 bg-red-50 border border-red-300 rounded-lg hover:bg-red-100 transition-colors"
            >
              <Pause className="w-4 h-4" />
              Suspend Customer
            </button>
          ) : (
            <button
              onClick={handleActivate}
              className="inline-flex items-center gap-2 px-4 py-2 text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-lg hover:bg-emerald-100 transition-colors"
            >
              <Play className="w-4 h-4" />
              Activate Customer
            </button>
          )}
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-2 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete Customer
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      <EditDistributorModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={fetchData}
        distributorId={id}
      />

      {/* Account Balance Modal */}
      <AccountBalanceModal
        isOpen={isAccountBalanceModalOpen}
        onClose={() => setIsAccountBalanceModalOpen(false)}
        distributorId={id}
        distributorName={distributor?.business_name || ''}
        accountBalance={parseFloat(creditSummary?.account_balance || 0)}
        outstandingBalance={creditUsed}
        onSuccess={fetchData}
      />

      {/* Transfer Modal */}
      <TransferBalanceModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        fromDistributorId={id}
        fromDistributorName={distributor?.business_name || ''}
        accountBalance={parseFloat(creditSummary?.account_balance || 0)}
        onSuccess={fetchData}
      />

      {/* Refund Modal */}
      <RefundBalanceModal
        isOpen={isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
        distributorId={id}
        distributorName={distributor?.business_name || ''}
        accountBalance={parseFloat(creditSummary?.account_balance || 0)}
        onSuccess={fetchData}
      />
    </div>
  );
};

// Transfer Balance Modal Component
const TransferBalanceModal = ({
  isOpen,
  onClose,
  fromDistributorId,
  fromDistributorName,
  accountBalance,
  onSuccess
}) => {
  const [toDistributorId, setToDistributorId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [distributors, setDistributors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchDistributors();
    }
  }, [isOpen]);

  const fetchDistributors = async () => {
    try {
      const res = await getDistributors({ limit: 100 });
      const filtered = (res?.data || res || []).filter((d) => d.id !== parseInt(fromDistributorId));
      setDistributors(filtered);
    } catch (err) {
      console.error('Error fetching distributors:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!toDistributorId || !amount || parseFloat(amount) <= 0) {
      toast.error('Please fill all required fields');
      return;
    }
    if (parseFloat(amount) > accountBalance) {
      toast.error('Amount exceeds available balance');
      return;
    }

    setLoading(true);
    try {
      await transferAccountBalance(
        fromDistributorId,
        toDistributorId,
        parseFloat(amount),
        description
      );
      toast.success('Balance transferred successfully');
      onSuccess();
      onClose();
      setToDistributorId('');
      setAmount('');
      setDescription('');
    } catch (err) {
      toast.error(err.message || 'Failed to transfer balance');
    } finally {
      setLoading(false);
    }
  };

  const filteredDistributors = distributors.filter(
    (d) =>
      d.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.distributor_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Transfer Balance
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-sm text-blue-700">
                From: <span className="font-semibold">{fromDistributorName}</span>
              </p>
              <p className="text-sm text-blue-600">
                Available: <span className="font-semibold">₦{accountBalance.toLocaleString()}</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transfer To *</label>
              <input
                type="text"
                placeholder="Search customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
              />
              <select
                value={toDistributorId}
                onChange={(e) => setToDistributorId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              >
                <option value="">Select customer</option>
                {filteredDistributors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.business_name} ({d.distributor_number})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                max={accountBalance}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Reason for transfer"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Refund Balance Modal Component
const RefundBalanceModal = ({
  isOpen,
  onClose,
  distributorId,
  distributorName,
  accountBalance,
  onSuccess
}) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [refundMethod, setRefundMethod] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0 || !refundMethod) {
      toast.error('Please fill all required fields');
      return;
    }
    if (parseFloat(amount) > accountBalance) {
      toast.error('Amount exceeds available balance');
      return;
    }

    setLoading(true);
    try {
      await refundAccountBalance(distributorId, parseFloat(amount), description, refundMethod);
      toast.success('Refund processed successfully');
      onSuccess();
      onClose();
      setAmount('');
      setDescription('');
      setRefundMethod('');
    } catch (err) {
      toast.error(err.message || 'Failed to process refund');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-red-600" />
              Refund Balance
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
              <p className="text-sm text-red-700">
                Customer: <span className="font-semibold">{distributorName}</span>
              </p>
              <p className="text-sm text-red-600">
                Available: <span className="font-semibold">₦{accountBalance.toLocaleString()}</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Refund Amount *
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                max={accountBalance}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Refund Method *
              </label>
              <select
                value={refundMethod}
                onChange={(e) => setRefundMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              >
                <option value="">Select method</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="mobile_money">Mobile Money</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Reason for refund"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DistributorDetailPage;
